/**
 * Invoice issuer header configuration.
 *
 * E-invoices (Memberships / Membership Renewals / Commissions) display the
 * issuing company at the top of the document. When a Brand World admin is
 * logged in, the header should show Brand World as the issuer (since BW is
 * buyer & seller in every transaction); BXI admins continue to see BXI.
 *
 * Per product spec (2026-05): BW reuses BXI's physical address, tel, email,
 * and website. Only the company name and GSTIN change. Replace
 * `BRAND_WORLD_GSTIN_PLACEHOLDER` with the actual GSTIN once supplied.
 */

const BXI_ISSUER = Object.freeze({
  companyName: 'BXI WORLD LLP',
  addressLine1:
    'Unit Part B on 3rd floor, VKG Corporate Centre, Andheri Kurla Road, opp. IRA Hotel',
  addressLine2: 'Andheri East, Mumbai - 400 059.',
  tel: '022 - 49646776',
  gstin: '27AAXFB2929C1ZA',
  website: 'www.bxiworld.com',
  email: 'support@bxiworld.com',
});

const BRAND_WORLD_GSTIN_PLACEHOLDER = '27ABBFB2363D1ZW';

const BRAND_WORLD_ISSUER = Object.freeze({
  ...BXI_ISSUER,
  companyName: 'BRAND WORLD',
  gstin: BRAND_WORLD_GSTIN_PLACEHOLDER,
});

/** Canonical Brand World company _id (buyer & seller in every BW transaction). */
export const BRAND_WORLD_COMPANY_ID = '699716f77c696eee9e6ee8ac';

/** Brand World logo shown on PIs / invoices in place of a member company logo. */
export const BRAND_WORLD_LOGO_URL =
  'https://bxi-icons.sfo3.cdn.digitaloceanspaces.com/BrandWorldLogo.svg';

export const BRAND_WORLD_PHONE = '9867738393';

/** Single-line Brand World address (reuses BXI's physical address, per spec). */
export const BRAND_WORLD_ADDRESS_LINE =
  `${BRAND_WORLD_ISSUER.addressLine1}, ${BRAND_WORLD_ISSUER.addressLine2}`;

/**
 * @param {string | Record<string, any> | null | undefined} addr
 * @returns {string}
 */
export function formatInvoiceAddress(addr) {
  if (!addr) return '';
  if (typeof addr === 'string') return addr.trim();
  if (typeof addr !== 'object') return '';

  const line = String(
    addr.AddressLine || addr.Street || addr.address || addr.Address || ''
  ).trim();

  const extras = [addr.City, addr.District, addr.State, addr.PinCode || addr.Pincode]
    .map((part) => (part != null ? String(part).trim() : ''))
    .filter(Boolean)
    .filter((part) => !line.toLowerCase().includes(part.toLowerCase()));

  const country = String(addr.Country || '').trim();
  if (country && country.toLowerCase() !== 'india') {
    const countryLower = country.toLowerCase();
    if (
      !line.toLowerCase().includes(countryLower) &&
      !extras.some((part) => part.toLowerCase() === countryLower)
    ) {
      extras.push(country);
    }
  }

  if (!line && extras.length === 0) return '';
  if (!line) return extras.join(', ');
  if (extras.length === 0) return line;
  return `${line}, ${extras.join(', ')}`;
}

/**
 * True when an invoice party (SellerDetails / BuyerDetails sub-document) is
 * Brand World — matched either by company id or by name. Tolerant of either the
 * Seller* or Buyer* field naming so it works for whichever side BW is on.
 *
 * @param {Record<string, any> | null | undefined} party
 * @returns {boolean}
 */
export function isBrandWorldParty(party) {
  if (!party || typeof party !== 'object') return false;
  const id = String(
    party.SellerCompanyId ?? party.BuyerCompanyId ?? party.CompanyId ?? ''
  );
  if (id && id === BRAND_WORLD_COMPANY_ID) return true;
  const name = String(
    party.SellerCompanyName ?? party.BuyerCompanyName ?? party.companyName ?? ''
  );
  return /brand\s*world/i.test(name);
}

/**
 * Returns the issuer header to render on e-invoices for the current admin.
 *
 * @param {string | undefined | null} adminType Output of `user?.adminType`
 *   from `useGetLoggedInUser` — `'bxi'` or `'brand_world'`.
 * @returns {typeof BXI_ISSUER} Frozen issuer config.
 */
export function getInvoiceIssuer(adminType) {
  if (String(adminType || '').toLowerCase() === 'brand_world') {
    return BRAND_WORLD_ISSUER;
  }
  return BXI_ISSUER;
}

export const BXI_INVOICE_ISSUER = BXI_ISSUER;
export const BRAND_WORLD_INVOICE_ISSUER = BRAND_WORLD_ISSUER;
